import { DateTime } from 'luxon';

/** @typedef {import('../../constants').AsnBox} AsnBox */
/** @typedef {import('../../constants').AsnProduct} AsnProduct */
/** @typedef {import('../../constants').AsnExpectedProduct} AsnExpectedProduct */
/** @typedef {import('../../constants').AdvancedShippingNotice} AdvancedShippingNotice */
/** @typedef {import('../../constants').AsnFreightConfiguration} AsnFreightConfiguration */

import { SETUP_CONFIGURATION_STEP, SELECT_PRODUCTS_STEP, DEFINE_SHIPMENTS_STEP, DEFAULT_BOXES_PER_CONFIG } from './steps/constants';

/**
 * Calculates the total quantity of each product across all boxes
 * @param {Array<AsnBox>} boxes - Array of boxes containing products
 * @returns {{[productId: string]: number}} Object mapping productId to total quantity
 */
const calculateProductTotals = (boxes) => {
  return boxes
    .flatMap((box) => box.products)
    .reduce(
      (acc, product) => ({
        ...acc,
        [product.productId]: (acc[product.productId] || 0) + product.quantity,
      }),
      {},
    );
};

/**
 * Maps a box product to include additional quantity information
 * @param {AsnProduct} boxProduct - The product in the box
 * @param {AsnExpectedProduct} expectedProduct - The expected product details
 * @param {{[productId: string]: number}} productTotalsByProductId - Map of total quantities by productId
 * @returns {object} Mapped product with additional quantity information
 */
const mapBoxProduct = (boxProduct, expectedProduct, productTotalsByProductId) => ({
  ...boxProduct,
  quantityPerBox: boxProduct?.quantity || 0,
  quantity: expectedProduct?.quantity || 0,
  quantityRemaining: expectedProduct?.quantity - (productTotalsByProductId[boxProduct.productId] || 0),
});

/**
 * Consolidates boxes by their configuration ID and adds quantity information
 * @param {Array<AsnBox>} boxes - Array of boxes to consolidate
 * @param {Array<AsnExpectedProduct>} products - Array of expected products
 * @returns {Array<AsnBox>} Consolidated boxes with quantity information
 */
const consolidateBoxesByConfigurationId = (boxes, products) => {
  if (!boxes) return [];

  const productTotalsByProductId = calculateProductTotals(boxes);

  const boxesByConfiguration = boxes.reduce((grouped, box) => {
    const configId = box.boxConfigurationId;
    const mappedProducts = box.products.map((boxProduct) => {
      const expectedProduct = products.find((p) => p.productId === boxProduct.productId);
      return mapBoxProduct(boxProduct, expectedProduct, productTotalsByProductId);
    });

    const boxWithMappedProducts = { ...box, products: mappedProducts };
    if (!grouped[configId]) {
      return {
        ...grouped,
        [configId]: {
          ...boxWithMappedProducts,
          boxesPerConfig: DEFAULT_BOXES_PER_CONFIG,
        },
      };
    }
    return {
      ...grouped,
      [configId]: {
        ...grouped[configId],
        boxesPerConfig: grouped[configId].boxesPerConfig + 1,
      },
    };
  }, {});

  return Object.values(boxesByConfiguration);
};

/**
 * Checks if a step is complete by verifying all its key values are not empty
 * @param {object} step - The step object to check
 * @returns {boolean} True if the step is complete, otherwise false
 */
export const isStepComplete = (step) => {
  return Object.values(step).every((value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== null && value !== undefined && value !== '';
  });
};

/**
 * Maps an Advanced Shipping Notice model to a step-based structure
 * @param {AdvancedShippingNotice} asnModel - The ASN model to transform
 * @returns {object} Step data organized by step constants
 * @property {object} SETUP_CONFIGURATION_STEP
 * @property {string} SETUP_CONFIGURATION_STEP.receivingCenter
 * @property {string} SETUP_CONFIGURATION_STEP.purchaseOrderNumber
 * @property {string} SETUP_CONFIGURATION_STEP.packageType
 * @property {object} SELECT_PRODUCTS_STEP
 * @property {Array<AsnExpectedProduct>} SELECT_PRODUCTS_STEP.selectedProducts
 * @property {object} DEFINE_SHIPMENTS_STEP
 * @property {Array<AsnBox>} DEFINE_SHIPMENTS_STEP.boxes
 * @property {boolean} DEFINE_SHIPMENTS_STEP.isComplete
 * @property {AsnFreightConfiguration} SCHEDULE_FREIGHT_STEP
 * @property {DateTime} SCHEDULE_FREIGHT_STEP.confirmedDropOffDate
 * @property {DateTime} SCHEDULE_FREIGHT_STEP.dropOffOption1
 * @property {DateTime} SCHEDULE_FREIGHT_STEP.dropOffOption2
 * @property {DateTime} SCHEDULE_FREIGHT_STEP.dropOffOption3
 * @property {AsnContact} SCHEDULE_FREIGHT_STEP.carrierContact
 * @property {AsnContact} SCHEDULE_FREIGHT_STEP.tenantContact
 * @property {string} SCHEDULE_FREIGHT_STEP.deliveryNote
 * @property {string} SCHEDULE_FREIGHT_STEP.freightCarrier
 */
export const mapAsnToSteps = (asnModel) => {
  const createStep = (data) => ({
    ...data,
    isComplete: isStepComplete(data),
  });

  const setupConfigurationStep = createStep({
    receivingCenter: asnModel.receivingCenter,
    purchaseOrderNumber: asnModel.purchaseOrderNumber,
    packageType: asnModel.packageType,
  });

  const selectedProductsStep = createStep({
    selectedProducts: asnModel.expectedProducts,
  });

  const defineShipmentsStep = createStep({
    boxes: consolidateBoxesByConfigurationId(asnModel.boxes, asnModel.expectedProducts),
  });

  return {
    [SETUP_CONFIGURATION_STEP]: setupConfigurationStep,
    [SELECT_PRODUCTS_STEP]: selectedProductsStep,
    [DEFINE_SHIPMENTS_STEP]: defineShipmentsStep,
  };
};

/**
 * Creates a new box object from an original box configuration, filling in missing properties with defaults
 * @param {AsnBox} originalBox - The original box configuration to copy from
 * @returns {AsnBox} A new box object with all required properties
 */
const createBox = (originalBox) => ({
  boxId: originalBox.boxId || '',
  boxConfigurationId: originalBox.boxConfigurationId || '',
  trackingNumber: originalBox.trackingNumber || '',
  products: originalBox.products.map((product) => ({
    productId: product.productId || '',
    name: product.name || '',
    sku: product.sku || '',
    lotNumber: product.lotNumber || null,
    expirationDate: product.expirationDate || null,
    productIdentifiers: product.productIdentifiers || {},
    quantity: product.quantityPerBox || 0,
    quantityPerBox: product.quantityPerBox || 0,
  })),
  shippingConfiguration: originalBox.shippingConfiguration || null,
  boxesPerConfig: originalBox.boxesPerConfig || DEFAULT_BOXES_PER_CONFIG,
});

/**
 * Creates multiple copies of a box configuration
 * @param {AsnBox} originalBox - The box configuration to copy
 * @param {number} count - Number of copies to create
 * @returns {Array<AsnBox>} Array of box copies
 */
const createBoxCopies = (originalBox, count) => Array.from({ length: count }, () => createBox(originalBox));

/**
 * Creates multiple copies of boxes based on their boxesPerConfig value
 * @param {Array<AsnBox>} defineShipmentsStepBoxes - Array of box configurations from the Define Shipments step
 * @returns {Array<AsnBox>} Array of expanded boxes
 */
const createBoxesFromShipmentStep = (defineShipmentsStepBoxes) => {
  if (!defineShipmentsStepBoxes) return [];

  return defineShipmentsStepBoxes.flatMap((originalBox) => {
    const { boxesPerConfig = DEFAULT_BOXES_PER_CONFIG } = originalBox;
    return createBoxCopies(originalBox, boxesPerConfig);
  });
};

/**
 *
 * @param {string} asnId
 * @param {object} stepPayloads
 * @returns {AdvancedShippingNotice}
 * @function
 */
export const mapStepsToAsn = (asnId, stepPayloads) => {
  const setupConfigurationStep = stepPayloads[SETUP_CONFIGURATION_STEP];
  const selectProductsStep = stepPayloads[SELECT_PRODUCTS_STEP];
  const defineShipmentsStep = stepPayloads[DEFINE_SHIPMENTS_STEP];

  return {
    asnId,
    partnerAsnId: '',
    clientId: '',
    createdAt: undefined,
    modifiedAt: undefined,
    submittedAt: undefined,
    status: 'Draft',
    purchaseOrderNumber: setupConfigurationStep?.purchaseOrderNumber,
    packageType: setupConfigurationStep?.packageType,
    receivingCenter: setupConfigurationStep?.receivingCenter,
    expectedProducts: selectProductsStep?.selectedProducts,
    boxes: createBoxesFromShipmentStep(defineShipmentsStep?.boxes),
    // TODO: remove this once we have a real expected arrival date
    expectedArrivalDate: DateTime.utc().plus({ days: 5 }).toISO(),
    freightConfiguration: undefined, // NOTE: this will get upated by the freight schedule modal
    parcelConfigurations: [],
    hasBoxLabels: false,
  };
};
